import { Alert, Button, Checkbox, Col, Form, Input, InputNumber, Row, Segmented, Select, Space, Spin, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { useModal } from "../../core/context/ModalContext";
import { CopyOutlined, ExportOutlined, LinkOutlined, MailOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import { writeClipboard } from "../../core/utils/clipboard-utils";

const EMAIL_PATTERN = /^[\w\-.+]+@([\w-]+\.)+[\w-]{2,}$/;

// Hazır süreler (saat). Listede olmayan bir değer "Özel" sayılır.
const VALIDITY_PRESETS = [24, 72, 168, 720];

// Seçenek alanları: biri değişince oluşturulmuş linkler geçersiz sayılır (mail aynı linkleri taşıyacağı
// için ekrandaki linkler her zaman ekrandaki seçenekleri yansıtmalı).
const OPTION_FIELDS = ['validityHours', 'description', 'includeWebViewer', 'includeDownload', 'includeViewer', 'includeReports'];

const INITIAL_VALUES = {
    validityHours: 72,
    description: '',
    includeWebViewer: true,
    includeDownload: true,
    includeViewer: true,
    includeReports: true,
    emailAddresses: []
};

// Paylaşım paneli: ne paylaşılacağı solda, nasıl paylaşılacağı (link / e-posta) sağda. Linkler bir kez
// oluşturulur; e-posta sekmesi de aynı linkleri (grup anahtarıyla) gönderir, ikinci bir paylaşım açılmaz.
export default function StudyShareModal({ studyIds = [] }) {
    const { t } = useTranslation();
    const { closeModal } = useModal();
    const [form] = Form.useForm();

    const [activeTab, setActiveTab] = useState('link');
    const [creating, setCreating] = useState(false);
    const [sending, setSending] = useState(false);
    const [shareLinks, setShareLinks] = useState([]);
    const [customValidity, setCustomValidity] = useState(false);

    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [subject, setSubject] = useState('');
    const [subjectEdited, setSubjectEdited] = useState(false);
    const previewSeq = useRef(0);

    const validityHours = Form.useWatch('validityHours', form);
    const description = Form.useWatch('description', form);
    const includeWebViewer = Form.useWatch('includeWebViewer', form);
    const includeDownload = Form.useWatch('includeDownload', form);
    const includeViewer = Form.useWatch('includeViewer', form);
    const includeReports = Form.useWatch('includeReports', form);

    const options = useMemo(() => ({
        validityHours: validityHours ?? INITIAL_VALUES.validityHours,
        description: description || null,
        includeWebViewer: includeWebViewer ?? true,
        includeDownload: includeDownload ?? true,
        includeViewer: includeViewer ?? true,
        includeReports: includeReports ?? true
    }), [validityHours, description, includeWebViewer, includeDownload, includeViewer, includeReports]);

    const nothingSelected = !options.includeWebViewer && !options.includeDownload && !options.includeReports;
    const validityValid = options.validityHours >= 1 && options.validityHours <= 720;
    const canShare = !nothingSelected && validityValid;

    const onValuesChange = (changed) => {
        if (shareLinks.length > 0 && Object.keys(changed).some(k => OPTION_FIELDS.includes(k))) {
            setShareLinks([]);
        }
    };

    const createLinks = async () => {
        const response = await ApiService.StudyShare.CreateShareLinks({
            studyIdList: studyIds,
            ...options
        });

        if (!response.success) {
            return null;
        }

        setShareLinks(response.data);
        return response.data;
    };

    const handleCreateLinks = async () => {
        try {
            setCreating(true);
            await createLinks();
        } finally {
            setCreating(false);
        }
    };

    // E-posta önizlemesi: sekme açıkken seçenekler değiştikçe (kısa gecikmeyle) sunucuda işlenir —
    // gönderimde kullanılan şablon ve işleyicinin aynısı.
    useEffect(() => {
        if (activeTab !== 'email' || !canShare) {
            return;
        }

        const seq = ++previewSeq.current;
        const timer = setTimeout(async () => {
            setPreviewLoading(true);
            const response = await ApiService.StudyShare.PreviewEmail({
                studyIds,
                ...options,
                subject: subjectEdited ? subject : null
            });

            if (seq !== previewSeq.current) {
                return;
            }

            setPreviewLoading(false);
            if (response.success) {
                setPreview(response.data);
                setPreviewError(null);
                if (!subjectEdited) {
                    setSubject(response.data.subject);
                }
            } else {
                setPreviewError(response.message || t('Email preview could not be loaded'));
            }
        }, 400);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, options, canShare]);

    const handleSendEmail = async () => {
        try {
            const values = await form.validateFields(['emailAddresses']);
            setSending(true);

            const response = await ApiService.StudyShare.SendShareLinksByEmail({
                studyIds,
                ...options,
                emailAddresses: values.emailAddresses,
                subject: subject?.trim() || null,
                // Ekranda oluşturulmuş linkler varsa mail onları taşır; yoksa gönderimde oluşturulur.
                groupKey: shareLinks[0]?.groupKey || null
            });

            if (response.success) {
                toast.success(t('Emails sent successfully'));
                closeModal();
            }
        } catch (error) {
            // Alıcı alanındaki doğrulama hatası formda gösteriliyor.
        } finally {
            setSending(false);
        }
    };

    // Güvenli olmayan bağlam / izin reddi / jest kopması durumlarının tamamı writeClipboard içinde
    // ele alınıyor (core/utils/clipboard-utils.js); burada sadece sonucu kullanıcıya bildiriyoruz.
    const copy = async (text, successMessage = t('Link copied to clipboard')) => {
        if (await writeClipboard(text)) {
            toast.success(successMessage);
        } else {
            toast.error(t('Could not copy the link'));
        }
    };

    const expiryText = shareLinks.length > 0 ? moment(shareLinks[0].expiryDate).format('DD.MM.YYYY HH:mm') : null;
    const viewerUrl = shareLinks[0]?.viewerUrl;

    // Alıcıya yapıştırılacak metin: seçilen içeriklere göre, okunur biçimde.
    const buildAllText = () => {
        const lines = [];

        if (options.description) {
            lines.push(options.description, '');
        }

        lines.push(`${t('Valid until')}: ${expiryText}`, '');

        if (options.includeWebViewer && viewerUrl) {
            lines.push(`${t('View in browser (all studies)')}:`, viewerUrl, '');
        }

        if (options.includeDownload || options.includeReports) {
            shareLinks.forEach(link => {
                lines.push([link.patientName, link.modality, link.studyDescription, link.accessionNumber]
                    .filter(x => x && x !== '*')
                    .join(' - '));

                if (options.includeDownload) {
                    lines.push(`${t('Download')}: ${link.downloadUrl}`);
                }

                if (options.includeReports) {
                    (link.reports || []).forEach(report => lines.push(`${report.title}: ${report.downloadUrl}`));
                }

                lines.push('');
            });
        }

        return lines.join('\n').trim();
    };

    const columns = [
        {
            title: t('Patient Name'),
            dataIndex: 'patientName',
            key: 'patientName',
            ellipsis: true
        },
        {
            title: t('Accession Number'),
            dataIndex: 'accessionNumber',
            key: 'accessionNumber'
        },
        {
            title: t('Modality'),
            dataIndex: 'modality',
            key: 'modality',
            width: 90
        },
        {
            title: t('Study Description'),
            dataIndex: 'studyDescription',
            key: 'studyDescription',
            ellipsis: true
        },
        options.includeDownload && {
            title: t('Download Link'),
            key: 'download',
            width: 130,
            render: (_, row) => (
                <Space size={4}>
                    <Tooltip title={t('Copy')}>
                        <Button size="small" icon={<CopyOutlined />} onClick={() => copy(row.downloadUrl)} />
                    </Tooltip>
                    <Tooltip title={t('Open link')}>
                        <Button size="small" icon={<ExportOutlined />} href={row.downloadUrl} target="_blank" rel="noopener noreferrer" />
                    </Tooltip>
                </Space>
            )
        },
        options.includeReports && {
            title: t('Reports'),
            key: 'reports',
            width: 160,
            render: (_, row) => (row.reports?.length > 0
                ? (
                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copy(row.reports.map(r => `${r.title}: ${r.downloadUrl}`).join('\n'))}
                    >
                        {t('{{count}} report(s)', { count: row.reports.length })}
                    </Button>
                )
                : <Typography.Text type="secondary">{t('No confirmed report')}</Typography.Text>)
        }
    ].filter(Boolean);

    const linkTab = (
        <div>
            {shareLinks.length === 0 ? (
                <div style={{ padding: '32px 0', textAlign: 'center' }}>
                    <Typography.Paragraph type="secondary">
                        {t('Links have not been created yet. They will reflect the options on the left.')}
                    </Typography.Paragraph>
                    <Button type="primary" icon={<LinkOutlined />} loading={creating} disabled={!canShare} onClick={handleCreateLinks}>
                        {t('Create Links')}
                    </Button>
                </div>
            ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Alert
                        type="success"
                        showIcon
                        message={t('{{count}} study shared, valid until {{date}}', { count: shareLinks.length, date: expiryText })}
                        description={t('Changing an option on the left discards these links; create them again afterwards.')}
                        action={
                            <Button icon={<CopyOutlined />} onClick={() => copy(buildAllText(), t('All links copied to clipboard'))}>
                                {t('Copy All')}
                            </Button>
                        }
                    />

                    {options.includeWebViewer && viewerUrl && (
                        <div>
                            <Typography.Text strong>{t('View in browser (all studies)')}</Typography.Text>
                            <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                                <Input value={viewerUrl} readOnly />
                                <Tooltip title={t('Copy')}>
                                    <Button icon={<CopyOutlined />} onClick={() => copy(viewerUrl, t('Viewer link copied to clipboard'))} />
                                </Tooltip>
                                <Tooltip title={t('Open link')}>
                                    <Button icon={<ExportOutlined />} href={viewerUrl} target="_blank" rel="noopener noreferrer" />
                                </Tooltip>
                            </Space.Compact>
                        </div>
                    )}

                    {(options.includeDownload || options.includeReports) && (
                        <Table
                            dataSource={shareLinks}
                            columns={columns}
                            rowKey="studyId"
                            pagination={false}
                            size="small"
                            scroll={{ y: 260 }}
                        />
                    )}
                </Space>
            )}
        </div>
    );

    const emailTab = (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Form.Item
                label={t('Recipients')}
                name="emailAddresses"
                style={{ marginBottom: 8 }}
                extra={t('Type an address and press Enter, comma or space.')}
                rules={[
                    { required: true, type: 'array', min: 1, message: t('Please enter at least one email address') },
                    {
                        validator: (_, value) => {
                            const invalid = (value || []).filter(x => !EMAIL_PATTERN.test(x));
                            return invalid.length === 0
                                ? Promise.resolve()
                                : Promise.reject(new Error(`${t('Invalid email address')}: ${invalid.join(', ')}`));
                        }
                    }
                ]}
            >
                <Select
                    mode="tags"
                    open={false}
                    suffixIcon={null}
                    tokenSeparators={[',', ';', ' ']}
                    placeholder="ornek@hastane.com"
                    tagRender={({ label, value, closable, onClose }) => (
                        <Tag
                            color={EMAIL_PATTERN.test(value) ? undefined : 'error'}
                            closable={closable}
                            onClose={onClose}
                            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                            style={{ marginInlineEnd: 4 }}
                        >
                            {label}
                        </Tag>
                    )}
                />
            </Form.Item>

            <Form.Item label={t('Subject')} style={{ marginBottom: 8 }}>
                <Input
                    value={subject}
                    maxLength={200}
                    onChange={e => {
                        setSubject(e.target.value);
                        setSubjectEdited(true);
                    }}
                />
            </Form.Item>

            {preview && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('From')}: <b>{preview.fromName}</b>{preview.fromAddress ? ` <${preview.fromAddress}>` : ''}
                    {' · '}
                    {t('Replies go to')}: {preview.replyTo || t('not set (system address)')}
                </Typography.Text>
            )}

            {preview?.multipleHospitals && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('The selected studies belong to more than one hospital. The template and sender of {{hospital}} will be used.', { hospital: preview.hospitalName })}
                />
            )}

            {preview?.templateIsDefault && (
                <Alert
                    type="info"
                    showIcon
                    message={t('{{hospital}} has no custom email template; the default template is used. It can be edited under Hospitals → Share Email.', { hospital: preview.hospitalName })}
                />
            )}

            {previewError && <Alert type="error" showIcon message={previewError} />}

            <Spin spinning={previewLoading}>
                <iframe
                    title="email-preview"
                    sandbox=""
                    srcDoc={preview?.html || ''}
                    style={{ width: '100%', height: 380, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}
                />
            </Spin>

            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {shareLinks.length > 0
                    ? t('The links created in the Link tab will be sent.')
                    : t('Links are created when the email is sent. The preview shows placeholder links.')}
            </Typography.Text>
        </Space>
    );

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={INITIAL_VALUES}
            onValuesChange={onValuesChange}
        >
            <Row gutter={24}>
                <Col xs={24} lg={9}>
                    <Form.Item label={t('Validity')} required style={{ marginBottom: 8 }}>
                        <Segmented
                            block
                            value={customValidity || !VALIDITY_PRESETS.includes(validityHours) ? 'custom' : validityHours}
                            onChange={value => {
                                if (value === 'custom') {
                                    setCustomValidity(true);
                                    return;
                                }
                                setCustomValidity(false);
                                form.setFieldsValue({ validityHours: value });
                                onValuesChange({ validityHours: value });
                            }}
                            options={[
                                { label: t('24 hours'), value: 24 },
                                { label: t('3 days'), value: 72 },
                                { label: t('7 days'), value: 168 },
                                { label: t('30 days'), value: 720 },
                                { label: t('Custom'), value: 'custom' }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        name="validityHours"
                        hidden={!(customValidity || !VALIDITY_PRESETS.includes(validityHours))}
                        rules={[{ required: true, type: 'number', min: 1, max: 720, message: t('Value must be between 1 and 720') }]}
                    >
                        <InputNumber style={{ width: '100%' }} min={1} max={720} addonAfter={t('hours')} />
                    </Form.Item>

                    <Typography.Text strong>{t('What to share')}</Typography.Text>
                    <div style={{ marginTop: 8, marginBottom: 12 }}>
                        <Form.Item name="includeWebViewer" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>
                                {t('View in browser link')}
                                <div style={{ color: '#8c8c8c', fontSize: 12 }}>{t('Recipient opens all selected studies in the browser without downloading.')}</div>
                            </Checkbox>
                        </Form.Item>
                        <Form.Item name="includeDownload" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>
                                {t('DICOM download link (zip)')}
                                <div style={{ color: '#8c8c8c', fontSize: 12 }}>{t('One download link per study.')}</div>
                            </Checkbox>
                        </Form.Item>
                        <Form.Item name="includeViewer" valuePropName="checked" style={{ marginBottom: 4, marginLeft: 24 }}>
                            <Checkbox disabled={!options.includeDownload}>
                                {t('Add Windows viewer to the zip')}
                                <div style={{ color: '#8c8c8c', fontSize: 12 }}>{t('Offline CD viewer for recipients without DICOM software.')}</div>
                            </Checkbox>
                        </Form.Item>
                        <Form.Item name="includeReports" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>
                                {t('Confirmed reports (PDF)')}
                            </Checkbox>
                        </Form.Item>
                    </div>

                    {nothingSelected && (
                        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={t('Select at least one item to share.')} />
                    )}

                    <Form.Item
                        label={t('Description')}
                        name="description"
                        extra={t('Shown to the recipient in the email and in the copied text.')}
                    >
                        <Input.TextArea
                            rows={3}
                            maxLength={500}
                            placeholder={t('Optional note for the recipient')}
                            showCount
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} lg={15}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={[
                            { key: 'link', label: <span><LinkOutlined /> {t('Share by Link')}</span>, children: linkTab },
                            { key: 'email', label: <span><MailOutlined /> {t('Send by Email')}</span>, children: emailTab }
                        ]}
                    />
                </Col>
            </Row>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={closeModal}>
                    {t('Close')}
                </Button>
                {activeTab === 'email' && (
                    <Button
                        type="primary"
                        icon={<MailOutlined />}
                        loading={sending}
                        disabled={!canShare}
                        onClick={handleSendEmail}
                    >
                        {t('Send Email')}
                    </Button>
                )}
            </div>
        </Form>
    );
}
